import * as React from 'react';
import {transparentize} from 'polished';
import styled, {css} from 'styled-components';
import {transition, visuallyHidden} from 'helpers/css';
import {IconCheck} from 'icons';
import {Theme} from '../stores/AppStore';

export interface CheckboxProps extends
  React.InputHTMLAttributes<HTMLInputElement> {
  label?: string | React.ReactNode;
  scale?: CheckboxScale;
  accentLabel?: boolean;
  colorize?: string;
}

export enum CheckboxScale {
  Small = 'small',
  Medium = 'medium',
}

export const Label = styled.span<CheckboxProps>`
  font-weight: 500;
  user-select: none;
  ${transition()};

  ${({scale}) => {
    switch (scale) {
      case CheckboxScale.Medium: return css`
        font-size: 14px;
        line-height: 18px;
        font-weight: 400;
      `;
      default: return css`
       font-size: 12px;
       line-height: 16px;
     `;
    }
  }}
  
  ${({accentLabel, theme}) => {
    if (accentLabel) {
      return css `
        color: ${transparentize(.6, theme.variables.color)};
      `;
    }
  }}
`;

export const Content = styled.div`
  gap: 8px;
  align-items: center;
  grid-auto-flow: column;
  display: inline-grid;
`;

export const Checker = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 16px;
  width: 16px;
  border: 1px solid transparent;
  border-radius: 4px;
  background: ${({theme}) => transparentize(.90, theme.variables.color)};
  cursor: pointer;
  ${transition()};
`;

const Icon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transform: scale(0);
  ${transition()};
`;

const HiddenInput = styled.input`
  ${visuallyHidden}
  
  &:checked + ${Content} {
    ${Checker} {
      background: ${({theme}) => theme.colors.primary};
      ${Icon} {
        opacity: 1;
        transform: scale(1);
      }
    }
    ${Label} {
      color: ${({theme}) => theme.name === Theme.Dark ? theme.colors.white : '#000000'};
    }
  }
`;

const Container = styled.label<
  Pick<CheckboxProps, 'colorize'>
  >`
  display: inline-grid;
  cursor: pointer;

  ${({colorize}) => {
    if (colorize) {
      return css`
        ${HiddenInput} {
          &:checked + ${Content} {
            ${Checker} {
              border-color: ${colorize};
              background: transparent;
              
              ${Icon} {
                opacity: 1;
                transform: scale(1);
                color: ${colorize};
              }
            }
          }
        }
      `;
    }
  }};
`;

/**
 * The component render checkbox
 * @return {React.ReactElement}
 * @param {CheckboxProps} props
 * @constructor
 */
export default function Checkbox(props: CheckboxProps): React.ReactElement {
  const {label, scale, accentLabel, ...otherProps} = props;

  return (
    <Container
      colorize={props.colorize}
    >
      <HiddenInput
        {...otherProps}
        type="checkbox"
      />
      <Content>
        <Checker>
          <Icon>
            <IconCheck />
          </Icon>
        </Checker>
        {label && (
          <Label scale={scale} accentLabel={accentLabel}>
            {label}
          </Label>
        )}
      </Content>
    </Container>
  );
}
